import { Component, EventEmitter, Input, NgZone, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ModalBehavior, ModalsService } from "../../../../../../common/src/lib/services/modals.service";
import { SignatureComponent } from "../../../modals/signature/signature.component";
import { FormControl } from "@angular/forms";
import {
  AdditionalBusinessInfoForm, BankInformationForm, BusinessOwnersForm,
  DBAForm, DocumentType,
  LegalForm,
  SalesProfileForm
} from "../../../models/nuvei-application.model";
import { componentsToString } from "../../../../../../common/src/lib/pipes/address.pipe";
import { capitalizeFirstChar } from "../../../../../../common/src/lib/services";
import moment from "moment";
import { sqFootageDisplayValues } from "../nuvei-application-additional/nuvei-application-additional.component";
import { NuveiService, SavedNuveiApplicationForm, SubmitApplicationErrors } from "../../../services/nuvei.service";
import { take } from "rxjs/operators";
import { fileExt, UploadedFile } from "../nuvei-application-documents/nuvei-application-documents.component";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { BusinessService } from "../../../services/business.service";
import { serverToFrontTranslation } from "../../../../../../common/src/lib/services/supabase.service";

type AllNonNullable<T> = {
  [K in keyof T]-?: NonNullable<T[K]>;
};

function optionalField(value: any) {
  if(value === null || value === undefined || value === '')
    return '-';
  return value;
}

function unit(value: any) {
  if(value === null || value === undefined || value === '')
    return '';
  return ` (Unit: ${value})`;
}

function sameAsField<T, F extends keyof T>(values: T, field: F, other: F) {
  const sameAsOtherKey = `${field as string}SameAs${capitalizeFirstChar(other as string)}` as F;
  if(values[sameAsOtherKey])
    return values[other] as T[F];
  return values[field] as T[F];
}

type FieldType = 'phone' | '$' | '%';

type StepField = { name: string, value: string, type?: FieldType };
type Step = {
  title: string,
  fields: StepField[]
};

@Component({
  selector: 'app-nuvei-application-summary',
  templateUrl: './nuvei-application-summary.component.html',
  styleUrls: ['./nuvei-application-summary.component.scss']
})
export class NuveiApplicationSummaryComponent implements OnInit, OnChanges {

  fileExt = fileExt;

  @Input() files!: {
    [T in DocumentType]?: UploadedFile
  };
  @Input() legalForm!: LegalForm;
  @Input() dbaForm!: DBAForm;
  @Input() additionalBusinessInfoForm!: AdditionalBusinessInfoForm;
  @Input() salesProfileForm!: SalesProfileForm;
  @Input() businessOwnersForm!: BusinessOwnersForm;
  @Input() bankInformationForm!: BankInformationForm;
  @Input() currentSignatures!: SavedNuveiApplicationForm['signatures'];

  @Output() updateFormDocumentUrl = new EventEmitter<string>();
  @Output() onSignatureRevoke = new EventEmitter<string>();
  @Output() goToStep = new EventEmitter<number>();
  @Output() handleFormErrors = new EventEmitter<SubmitApplicationErrors>();
  @Output() checkServerDataMismatch = new EventEmitter();

  submitCheckboxControl = new FormControl<boolean>(false, { nonNullable: true });

  legalStep!: Step;
  dbaStep!: Step;
  additionalBusinessInfoStep!: Step;
  salesProfileStep!: Step;
  businessOwnersStep!: {
    title: string,
    fields: StepField[][]
  };
  bankInformationStep!: Step;

  openedOwner = -1;

  loading = true;

  get userName() {
    const owner = this.businessOwnersForm.owners![0];
    return `${owner.firstName} ${owner.lastName}`;
  }

  fileKeys!: DocumentType[];
  typeName = {
    'proofOfBusiness': 'Proof Of Business',
    'voidCheck': 'Void Check',
    'previousPaymentProcessor': 'Previous Payment Processor',
  };

  viewFileURL: SafeUrl | null = null;
  viewFileType: 'doc' | 'img' | 'txt' = 'doc';

  signatureApproval?: string;
  waitingForSignatureApproval = false;
  uploadingSignature = false;

  get ownerIsGuarantor() {
    return this.businessOwnersForm.guarantor === 0;
  }

  get guarantorEmail() {
    return this.businessOwnersForm.owners![this.businessOwnersForm.guarantor!].email;
  }

  constructor(
     private domSanitizer: DomSanitizer,
     private businessService: BusinessService,
     private nuveiService: NuveiService,
     private zone: NgZone,
     private modalsService: ModalsService
  ) {}

  async ngOnInit() {
    this.generateLegalStep();
    this.generateDBAStep();
    this.generateAdditionalBusinessInfoStep();
    this.generateSalesProfileStep();
    this.generateBusinessOwnersStep();
    this.generateBankInformationStep();
    this.fileKeys = (['proofOfBusiness', 'voidCheck', 'previousPaymentProcessor'] as DocumentType[])
        .filter(type => !!this.files[type]);
    this.loading = false;

    this.signatureApproval = this.currentSignatures.owner?.approval ?? undefined;
    this.submitCheckboxControl.setValue(!!this.currentSignatures.owner?.approval, { emitEvent: false });

    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    this.nuveiService.addEvent(business!.businessId, 'Preview');
  }

  ngOnChanges(changes: SimpleChanges) {
    if(changes.currentSignatures && !this.currentSignatures.owner?.approval) {
      this.submitCheckboxControl.setValue(false, { emitEvent: false });
    }
  }

  generateLegalStep() {
    const values = this.legalForm as AllNonNullable<LegalForm>;
    const mailingAddressComponents = sameAsField(values, 'mailingAddress', 'address');
    const mailingAddressUnit = values['mailingAddressSameAsAddress'] ? values['unit'] : values['mailingAddressUnit'];
    const mailingAddress = `${componentsToString(mailingAddressComponents)}${unit(mailingAddressUnit)}`
    this.legalStep = {
      title: 'Legal business information',
      fields: [
        { name: 'Business name', value: values.businessName },
        { name: 'Ownership type', value: values.ownershipType },
        { name: 'Address', value: `${componentsToString(values.address)}${unit(values.unit)}` },
        { name: 'Mailing address', value: mailingAddress },
        { name: 'Mailing attention', value: optionalField(values.mailingAttention) },
        { name: 'Business phone number', value: values.businessPhoneNumber, type: 'phone' },
        { name: 'Support phone number', value: sameAsField(values, 'supportPhoneNumber', 'businessPhoneNumber'), type: 'phone' },
        { name: 'Support phone number', value: sameAsField(values, 'contactPhoneNumber', 'businessPhoneNumber'), type: 'phone' },
        { name: 'Support phone number', value: sameAsField(values, 'mobilePhoneNumber', 'businessPhoneNumber'), type: 'phone' },
        { name: 'Business email address', value: values.businessEmail },
        { name: 'Contact email address', value: sameAsField(values, 'contactEmail', 'businessEmail') },
        { name: 'Business creation date', value: moment(values.creationDate).format('MM/DD/YYYY') },
        { name: 'Federal tax ID', value: values.federalTaxID },
        { name: 'Current payment processor', value: optionalField(values.currentPaymentProcessor) },
      ]
    };
  }

  generateDBAStep() {
    const values = this.dbaForm as AllNonNullable<DBAForm>;
    this.dbaStep = {
      title: 'Business display information (DBA)',
      fields: [
        { name: 'Business name', value: values.businessName },
        { name: 'Address', value: `${componentsToString(values.address)}${unit(values.unit)}` },
        { name: 'Phone number', value: values.phoneNumber, type: 'phone' },
        { name: 'Creation date', value: moment(values.creationDate).format('MM/DD/YYYY') },
        { name: 'Time zone', value: optionalField(values.timezone) },
      ]
    };
  }

  generateAdditionalBusinessInfoStep() {
    const values = this.additionalBusinessInfoForm as AllNonNullable<AdditionalBusinessInfoForm>;
    const refundPolicy = values.hasRefundPolicy ? values.refundPolicy : 'No refund policy';
    this.additionalBusinessInfoStep = {
      title: 'Additional business information',
      fields: [
        { name: 'Website', value: optionalField(values.website) },
        { name: 'Business type', value: values.businessType },
        { name: 'Business description', value: values.description },
        { name: 'Zoning', value: values.zoning },
        { name: 'Locale', value: values.locale },
        { name: 'Square footage of location', value: sqFootageDisplayValues[values.squareFootage] },
        { name: 'Refund policy', value: refundPolicy },
      ]
    };
  }

  generateSalesProfileStep() {
    const values = this.salesProfileForm as AllNonNullable<SalesProfileForm>;
    this.salesProfileStep = {
      title: 'Sales profile',
      fields: [
        { name: 'Avg. volume per month', value: `${values.avgVolumePerMonth}`, type: '$' },
        { name: 'Avg. ticket size', value: `${values.avgTicketSize}`, type: '$' },
        { name: 'High ticket', value: `${values.highTicket}`, type: '$' },
        { name: 'Expected AMEX percentage', value: `${values.expectedAMEXPercentage}`, type: '%' },
      ]
    };
  }

  generateBusinessOwnersStep() {
    const values = this.businessOwnersForm as AllNonNullable<BusinessOwnersForm>;
    this.businessOwnersStep = {
      title: `Business owner${values.owners.length > 1 ? 's' : ''} details`,
      fields: values.owners.map((owner, index) => [
        { name: 'Full name', value: `${owner.firstName} ${owner.lastName}` },
        { name: 'Phone number', value: `${owner.phoneNumber}`, type: 'phone' },
        { name: 'Email', value: `${owner.email}` },
        { name: 'Title', value: `${owner.title}` },
        { name: 'Percent ownership', value: `${owner.ownershipPercentage}`, type: '%' },
        { name: 'Citizenship', value: `${owner.citizenship}` },
        { name: 'Birth date', value: `${moment(owner.birthDate).format('MM/DD/YYYY')}` },
        { name: 'Social security number (SSN)', value: `${owner.ssn}` },
        { name: 'Address', value: `${componentsToString(owner.address!)}${unit(owner.unit)}` },
        { name: 'Is guarantor', value: index === values.guarantor ? 'Yes' : 'No' },
      ])
    };
    if(this.businessOwnersStep.fields.length === 1)
      this.openedOwner = 0;
  }

  generateBankInformationStep() {
    const values = this.bankInformationForm as AllNonNullable<BankInformationForm>;
    this.bankInformationStep = {
      title: 'Bank information',
      fields: [
        { name: 'Bank transit', value: `${values.bankTransit}` },
        { name: 'bank account number', value: `${values.bankAccountNumber}`},
        { name: 'Associated address', value: `${values.associatedAddress}` },
      ]
    };
  }

  ownerGroupClick(index: number) {
    if(this.businessOwnersStep.fields.length === 1)
      return;

    if(index === this.openedOwner)
      this.openedOwner = -1;
    else
      this.openedOwner = index;
  }

  viewFile(documentType: DocumentType) {
    const downloadUrl = this.files[documentType]!.downloadUrl;
    if(downloadUrl) {
      const url = new URL(downloadUrl);
      url.searchParams.delete('download');

      if(this.files[documentType]!.mimeType.includes('image'))
        this.viewFileType = 'img';
      else if(this.files[documentType]!.mimeType.includes('text'))
        this.viewFileType = 'txt';
      else
        this.viewFileType = 'doc';

      this.viewFileURL = this.domSanitizer.bypassSecurityTrustResourceUrl(url.toString());
    }
  }

  async signatureApprovalClick(event: MatCheckboxChange) {
    const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
    try {
      if(event.checked) {
        this.waitingForSignatureApproval = true;
        const approval = await this.nuveiService.requestApproval(business.businessId, 'signature');
        this.zone.run(() => this.signatureApproval = approval);
        this.waitingForSignatureApproval = false;
      } else {
        this.onSignatureRevoke.emit(this.signatureApproval);
        this.currentSignatures.owner = undefined;
        this.signatureApproval = undefined;
      }
    } catch (e: any) {
      const error = await e.context.json();
      if(error.message === "\"Owner not found\"") {
        this.checkServerDataMismatch.emit();
      }
      this.submitCheckboxControl.setValue(false);
      this.signatureApproval = undefined;
      this.waitingForSignatureApproval = false;
    }
  }

  async openSignatureModal() {
    if(!this.signatureApproval)
      throw new Error("Missing approval");

    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    this.nuveiService.addEvent(business!.businessId, 'Click To Sign');
    this.modalsService.open(SignatureComponent, {
      behavior: ModalBehavior.Dialog,
      data: {
        saveSignature: (async (data: string) => {
          const file = this.dataURItoBlob(data);
          const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
          try {
            await this.nuveiService.uploadSignature(business.businessId, file, this.signatureApproval!);
            this.zone.run(() => {
              this.currentSignatures.owner = {
                downloadUrl: data,
                signerName: this.userName,
                timestamp: new Date(),
                submitted: false,
                approval: this.signatureApproval!
              };
            });
          } catch (e: any) {
            const errors = serverToFrontTranslation(await e.context.json()) as SubmitApplicationErrors;
            this.handleFormErrors.emit(errors);
          }
        }).bind(this)
      },
    });
  }

  dataURItoBlob(dataURI: string): Blob {
    const byteString = atob(dataURI.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type: 'image/png' });
  }

  async onSubmitClick() {
    this.uploadingSignature = true;
    const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;
    try {

      const formDocumentUrl = await this.nuveiService.finalizeSignature(business.businessId);
      this.updateFormDocumentUrl.emit(formDocumentUrl);
      this.zone.run(() => {
        this.currentSignatures.owner!.submitted = true;
      });

      this.goToStep.emit(8);
    } catch (e: any) {
      const error = await e.context.json();
      if(error.message === "\"Owner not found\"") {
        this.checkServerDataMismatch.emit();
      } else {
        console.log(e);
      }
    }
    this.uploadingSignature = false;
  }

}
